import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { useApi, useResource, errorText } from "@/lib/api";
import { formatMoney, sumDecimals } from "@/lib/format";
import LedgerRow from "@/components/ledger/LedgerRow";
import QuickAddRow from "@/components/ledger/QuickAddRow";
import LedgerToolbar from "@/components/ledger/LedgerToolbar";
import PasteDialog from "@/components/ledger/PasteDialog";
import { outsideFy } from "@/components/ledger/rowRules";

const HEAD = ["Date", "Month", "Description", "Received", "Spent", "Category", "Notes", "For", "What", "Project", ""];
const PAGE = 150;

export default function LedgerGrid({ fy, initialFilter }) {
  const api = useApi();
  const { data: rows, setData, reload } = useResource(`/transactions?fy=${fy.label}`);
  const { data: categories } = useResource("/categories");
  const { data: dirs } = useResource("/directors");
  const directors = useMemo(() => (dirs || []).filter((d) => d.active), [dirs]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(initialFilter || "all");
  const [selected, setSelected] = useState(new Set());
  const [paste, setPaste] = useState({ open: false, text: "" });
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => { setLimit(PAGE); }, [search, filter]);

  useEffect(() => {
    const onPaste = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || paste.open) return;
      const text = e.clipboardData?.getData("text") || "";
      if (text.includes("\t") || text.includes("\n")) setPaste({ open: true, text });
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [paste.open]);

  const catType = useMemo(() => Object.fromEntries((categories || []).map((c) => [c.id, c.type])), [categories]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (q && !`${r.description} ${r.notes} ${r.for_whom || ""} ${r.what || ""} ${r.project || ""}`.toLowerCase().includes(q)) return false;
      if (filter === "blank") return !r.category;
      if (filter === "received") return !!r.received;
      if (filter === "spent") return !!r.spent;
      if (filter === "outside") return outsideFy(r.date, fy);
      if (filter.startsWith("acct:")) return r.bank_account === filter.slice(5);
      if (filter === "mismatch") {
        const t = catType[r.category];
        return (t === "income" && !!r.spent) || (t === "expense" && !!r.received);
      }
      if (filter !== "all") return r.category === filter;
      return true;
    }).sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id));
  }, [rows, search, filter, fy, catType]);

  const blankCount = (rows || []).filter((r) => !r.category).length;
  const accounts = useMemo(() => [...new Set((rows || []).map((r) => r.bank_account).filter(Boolean))].sort(), [rows]);
  const replace = useCallback((row) => setData((list) => list.map((r) => (r.id === row.id ? row : r))), [setData]);
  const remove = useCallback((id) => setData((l) => l.filter((x) => x.id !== id)), [setData]);
  const toggle = useCallback((id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const allSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));

  const bulk = async (category) => {
    try {
      const res = await api("post", "/transactions/bulk-category", { ids: [...selected], category });
      toast.success(`Updated ${res.updated} rows`);
      setSelected(new Set());
      reload();
    } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <div className="space-y-4" data-testid="ledger-grid">
      <LedgerToolbar search={search} setSearch={setSearch} filter={filter} setFilter={setFilter} categories={categories}
        blankCount={blankCount} accounts={accounts} selectedCount={selected.size} onBulk={bulk} onPaste={() => setPaste({ open: true, text: "" })} />
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="pl-3 py-3 w-8">
                <Checkbox checked={allSelected} data-testid="ledger-select-all"
                  onCheckedChange={(v) => setSelected(v ? new Set(visible.map((r) => r.id)) : new Set())} />
              </th>
              {HEAD.map((h) => <th key={h} className={`px-2 py-3 font-medium ${h === "Received" || h === "Spent" ? "text-right" : ""}`}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            <QuickAddRow fy={fy} categories={categories} onAdded={(row) => setData((l) => [...(l || []), row])} />
            {visible.slice(0, limit).map((r) => (
              <LedgerRow key={r.id} row={r} fy={fy} categories={categories} directors={directors} selected={selected.has(r.id)} onSelect={toggle}
                onSaved={replace} onDeleted={remove} />
            ))}
            {visible.length > limit && (
              <tr><td colSpan={12} className="px-4 py-4 text-center">
                <span className="text-sm text-muted-foreground mr-3">Showing {limit} of {visible.length}</span>
                <Button size="sm" variant="outline" className="rounded-full mr-2" onClick={() => setLimit((l) => l + PAGE * 2)} data-testid="ledger-show-more">Show more</Button>
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setLimit(visible.length)} data-testid="ledger-show-all">Show all</Button>
              </td></tr>
            )}
            {rows && visible.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-10 text-center text-muted-foreground">No rows. Add one above or paste from Excel.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t border-border text-xs">
              <td colSpan={4} className="px-3 py-3 text-muted-foreground" data-testid="ledger-row-count">{visible.length} rows shown</td>
              <td className="px-2 py-3 text-right font-mono text-emerald-300" data-testid="ledger-total-received">{formatMoney(sumDecimals(visible.map((r) => r.received)))}</td>
              <td className="px-2 py-3 text-right font-mono text-rose-300" data-testid="ledger-total-spent">{formatMoney(sumDecimals(visible.map((r) => r.spent)))}</td>
              <td colSpan={6} />
            </tr>
          </tfoot>
        </table>
      </div>
      <PasteDialog open={paste.open} onOpenChange={(o) => setPaste((p) => ({ ...p, open: o }))} text={paste.text}
        setText={(t) => setPaste((p) => ({ ...p, text: t }))} fy={fy} categories={categories} onImported={reload} />
    </div>
  );
}
